import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

/** "system" follows prefers-color-scheme live; light/dark are pinned. Mirrors index.html's
 *  pre-paint script, which applies the saved choice before React loads. */
export type ThemeChoice = "system" | "light" | "dark";
const KEY = "hs.theme";

function read(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

function apply(choice: ThemeChoice) {
  const dark = choice === "dark" || (choice === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.classList.toggle("dark", dark);
}

const ThemeContext = createContext<[ThemeChoice, (c: ThemeChoice) => void] | null>(null);

/** ONE theme state for the whole app, mounted at the root (main.tsx), so every screen (the login
 *  wall and access screens included, not only the signed-in shell) follows an OS switch live. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoice] = useState<ThemeChoice>(read);

  useEffect(() => {
    apply(choice);
    if (choice !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => apply("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [choice]);

  const set = useCallback((c: ThemeChoice) => {
    try {
      if (c === "system") localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, c);
    } catch {
      // Storage unavailable: the choice still applies for this page view.
    }
    setChoice(c);
  }, []);

  return <ThemeContext.Provider value={[choice, set]}>{children}</ThemeContext.Provider>;
}

export function useTheme(): [ThemeChoice, (c: ThemeChoice) => void] {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme outside <ThemeProvider>");
  return ctx;
}
